import { Button } from "@groot/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Textarea } from "@groot/ui/textarea";
import { useConfirm } from "@groot/ui/primitives";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Payslip } from "@/types";
import dayjs from "dayjs";
import { Eye, FileText, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Payslips() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [company, setCompany] = useState("");
  const [month, setMonth] = useState("");
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [payslipsData, companiesData] = await Promise.all([
        apiClient.getPayslips({ includeDetails: false }),
        apiClient.getPayslipCompanies(),
      ]);
      setPayslips(payslipsData);
      setCompanies(companiesData);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load payslips",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setPdfFiles(files);

      if (files.length === 1) {
        const file = files[0];
        // Try to infer month from filename if month is empty
        if (!month) {
          inferMonthFromFilename(file.name);
        }
      } else {
        // Reset month if switching to bulk upload, as it will be inferred per file
        setMonth("");
      }
    }
  };

  const inferMonthFromFilename = (filename: string) => {
    // Try to match YYYY-MM pattern in filename
    const monthPattern = /(\d{4}-\d{2})/;
    const match = filename.match(monthPattern);

    if (match) {
      const inferredMonth = match[1];
      // Validate that it's a valid YYYY-MM format
      const [year, monthNum] = inferredMonth.split("-").map(Number);
      if (year >= 2000 && year <= 2100 && monthNum >= 1 && monthNum <= 12) {
        setMonth(inferredMonth);
        toast({
          title: "Month detected",
          description: `Month "${inferredMonth}" detected from filename`,
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (pdfFiles.length === 0 || !company) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields",
      });
      return;
    }

    if (pdfFiles.length === 1 && !month) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a month",
      });
      return;
    }

    try {
      setUploading(true);

      if (pdfFiles.length > 1) {
        const result = await apiClient.bulkUploadPayslips({
          company,
          pdfs: pdfFiles,
        });

        if (result.failed > 0) {
          toast({
            title: "Bulk upload completed with errors",
            description: `Successfully uploaded ${result.successful} payslips. Failed: ${result.failed}. Check console for details.`,
            variant: "destructive",
          });
          console.error("Bulk upload errors:", result.errors);
        } else {
          toast({
            title: "Success",
            description: `Successfully uploaded ${result.successful} payslips.`,
          });
        }
      } else {
        await apiClient.uploadPayslip({
          company,
          month,
          pdf: pdfFiles[0],
          notes,
        });

        toast({
          title: "Success",
          description: "Payslip uploaded successfully",
        });
      }

      // Reset form
      setShowUploadForm(false);
      setCompany("");
      setMonth("");
      setPdfFiles([]);
      setNotes("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadData();
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to upload payslip",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleView = (id: number) => {
    navigate(`/payslips/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: "Are you sure you want to delete this payslip?",
      }))
    ) {
      return;
    }

    try {
      await apiClient.deletePayslip(id);
      toast({
        title: "Success",
        description: "Payslip deleted successfully",
      });
      await loadData();
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete payslip",
      });
    }
  };

  const formatMonth = (month: string) => {
    return dayjs(month, "YYYY-MM").format("MMMM YYYY");
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Payslips"
        description="Manage your monthly payslips"
        className="mb-8"
        actions={
          <Button onClick={() => setShowUploadForm(!showUploadForm)}>
            {showUploadForm ? (
              <>Cancel</>
            ) : (
              <>
                <Plus className="size-4" />
                Upload Payslip
              </>
            )}
          </Button>
        }
      />

      <div className="space-y-6">
        {showUploadForm && (
          <div className="border border-border rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  list="company-list"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Select or enter company name"
                  required
                />
                <datalist id="company-list">
                  {companies.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              {pdfFiles.length <= 1 && (
                <div>
                  <Label htmlFor="month">Month (YYYY-MM) *</Label>
                  <Input
                    id="month"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    placeholder="2025-01"
                    required={pdfFiles.length <= 1}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Will be auto-detected from filename if available
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="pdf">PDF File(s) *</Label>
                <Input
                  id="pdf"
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handleFileChange}
                  required
                />
                {pdfFiles.length > 0 && (
                  <div className="text-sm text-muted-foreground mt-1 space-y-1">
                    <p className="font-medium">
                      Selected: {pdfFiles.length} file
                      {pdfFiles.length !== 1 ? "s" : ""}
                    </p>
                    {pdfFiles.length > 1 && (
                      <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                        {pdfFiles.map((f, i) => (
                          <li key={`${f.name}-${i}`}>{f.name}</li>
                        ))}
                      </ul>
                    )}
                    {pdfFiles.length === 1 && <p>{pdfFiles[0].name}</p>}
                    {pdfFiles.length > 1 && (
                      <p className="text-xs text-warning">
                        Note: Month will be inferred from filenames (YYYY-MM)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {pdfFiles.length <= 1 && (
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes about this payslip"
                    rows={3}
                  />
                </div>
              )}

              <Button type="submit" disabled={uploading} className="w-full">
                {uploading ? (
                  <>
                    <Upload className="size-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="size-4" />
                    Upload Payslip
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Net pay</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                    <FileText className="mx-auto size-12 mb-3 opacity-20" />
                    <p className="text-sm">No payslips found</p>
                    <p className="text-xs mt-1">Upload your first payslip to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                payslips.map((payslip) => (
                  <TableRow key={payslip.id}>
                    <TableCell className="font-medium">{formatMonth(payslip.month)}</TableCell>
                    <TableCell>{payslip.company}</TableCell>
                    <TableCell className="text-muted-foreground">{payslip.fileName}</TableCell>
                    <TableCell className="text-right font-medium">
                      {payslip.netPay ? `₹${payslip.netPay.toLocaleString()}` : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleView(payslip.id)}>
                          <Eye className="size-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(payslip.id)}>
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Page>
  );
}
